import heic2any from "heic2any";
import React, { useState } from "react";
import supabase from "../../supabase";
import "./ImageUploader.css";
import { useNotification } from "../../context/NotificationContext";

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const ImageUploader = ({
  images,
  setImages,
  defaultImageIndex,
  setDefaultImageIndex,
}) => {
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const handleImageUpload = async (files) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/heic"];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    const newImagesPromises = Array.from(files).map(async (file) => {
      if (!allowedTypes.includes(file.type)) {
        showNotification(`Le format ${file.type} n'est pas supporté.`, "warning");
        return null;
      }

      if (file.size > maxSize) {
        showNotification(`Le fichier ${file.name} est trop volumineux.`, "warning");
        return null;
      }

      let fileToAdd = file;

      // Si le fichier est en HEIC, le convertir en JPEG
      if (file.type === "image/heic") {
        setLoading(true);
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
          });
          fileToAdd = new File(
            [convertedBlob],
            file.name.replace(/\.heic$/, ".jpg"),
            {
              type: "image/jpeg",
            }
          );
        } catch (error) {
          console.error(
            `Erreur lors de la conversion de ${file.name} :`,
            error
          );
          showNotification(`Impossible de convertir le fichier ${file.name}.`, "danger");
          setLoading(false);
          return null;
        } finally {
          setLoading(false);
        }
      }
      
      const base64Url = await toBase64(fileToAdd);

      return {
        file: fileToAdd,
        url: base64Url, 
        isNew: true, // Indique que c'est une nouvelle image
      };
    });

    const newImages = (await Promise.all(newImagesPromises)).filter(
      (img) => img !== null
    );
    setImages((prevImages) => [...prevImages, ...newImages]);
  };

  const handleSetDefaultImage = (index) => {
    setDefaultImageIndex(index);
  };

  const handleRemoveImage = async (index) => {
    const removedImage = images[index];

    if (removedImage?.id) {
      try {
        // Supprimer l'image de la base de données
        const { error } = await supabase
          .from("images")
          .delete()
          .eq("id", removedImage.id);

        if (error) {
          console.error("Erreur lors de la suppression de l'image :", error);
          showNotification("Une erreur est survenue lors de la suppression de l'image.", "danger");
          return;
        }
      } catch (error) {
        console.error("Erreur réseau lors de la suppression :", error);
        showNotification("Impossible de supprimer l'image. Veuillez réessayer.", "danger");
        return;
      }
    }

    // Supprimer l'image de l'état local
    setImages((prevImages) => {
      return prevImages.filter((_, i) => i !== index);
    });

    // Réinitialiser l'image par défaut si nécessaire
    if (index === defaultImageIndex) {
      setDefaultImageIndex(0);
    }
  };

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleImageUpload(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    handleImageUpload(e.target.files);
  };

  return (
    <div className="image-uploader-container">
      <div
        className={`drop-zone ${isDragOver ? "drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById("fileInput").click()}
      >
        <input
          type="file"
          id="fileInput"
          accept="image/*,.heic"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        {loading ? (
          <p>Conversion en cours, veuillez patienter...</p>
        ) : (
          <p>Glissez-déposez vos images ici ou cliquez pour sélectionner</p>
        )}
      </div>

      {images.length > 0 && (
        <div className="image-previews-grid mt-3">
          {images.map((img, index) => (
            <div key={img.id || index} className="image-preview-item">
              <img
                src={img.url || (img.file ? URL.createObjectURL(img.file) : "/placeholder.png")}
                alt={`Preview ${index}`}
                className="img-thumbnail"
              />
              <div className="image-actions">
                <button
                  className={`btn btn-sm ${index === defaultImageIndex ? "btn-warning" : "btn-outline-warning"}`}
                  onClick={() => handleSetDefaultImage(index)}
                  title="Définir comme image par défaut"
                >
                  <i className="fas fa-star"></i>
                </button>
                <button
                  className="btn btn-danger btn-sm ms-2"
                  onClick={() => handleRemoveImage(index)}
                  title="Supprimer l'image"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              {index === defaultImageIndex && (
                <div className="default-badge">Par défaut</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;